import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { isOttoSuperAdmin } from "../filterAssistant/core/ottoAccess";
import { getModels } from "./imageAnalysisService";

// What the whole Image Analyse page agrees on: which model the next analysis uses, and whether prices are
// on screen.
//
// A CONTEXT rather than props, because the two places that need it are not in a parent/child line: the run
// bar sits above the grid, and the findings tab is rendered INSIDE the image viewer's portal. Threading the
// selection down would mean adding Otto props to ImageMain and ImageViewModal - shared Image Management
// components - for state that is entirely Otto's. Context reaches through `createPortal` (the viewer tab is
// still in this tree), which is what makes "Re-analyse this image" honour the model chosen at the top.
//
// NOT REDUX, and nothing persisted. The selection is a decision about the next click, not application state:
// it is deliberately forgotten when the page is left, so nobody returns a week later and runs 20,000 images
// on a model they chose once for one photograph. (The house rule against new storage keys says the same.)
//
// PRICES ARE SUPER_ADMIN-ONLY AND START HIDDEN. The server is the enforcer - a curator's responses have no
// cost fields at all (`pricing_visible: false`) - so this only decides whether to draw what did arrive, and
// the toggle only appears for someone who has something to toggle. Default off is the ask: the page is for
// reading photographs, and a spend figure on screen is not everyone's business even when they may see it.

const ImageAnalysisSettingsContext = createContext(null);

// Used until GET /image_analysis/models answers; replaced by the backend's own default so the two cannot
// drift. Never sent to the API - a null model means "the configured default", which is this same value.
const FALLBACK = { model: null, models: [] };

export function ImageAnalysisSettingsProvider({ children }) {
    const [model, setModel] = useState(FALLBACK.model);
    const [models, setModels] = useState(FALLBACK.models);
    // Whether the SERVER will send prices to this user. Starts null (not asked yet) rather than false, so a
    // super_admin's toggle is there from the first paint instead of appearing when the request lands.
    const [pricingVisible, setPricingVisible] = useState(null);
    const [showPricing, setShowPricing] = useState(false); // ...and has this user asked to see them
    const canSeePricing = isOttoSuperAdmin();

    useEffect(() => {
        let cancelled = false;
        getModels()
            .then(res => {
                const data = (res && res.data) || {};
                if (cancelled) return;
                setModels(data.models || []);
                setPricingVisible(data.pricing_visible === true);
                // The backend's configured default, so the selector opens on what a run would use anyway.
                if (data.default) setModel(data.default);
            })
            .catch(() => {
                // The page still works with no selector: every call omits the model and the backend uses its
                // own default, which is exactly what this page did before the choice existed.
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const chosen = useMemo(() => models.find(m => m.id === model) || null, [models, model]);

    const toggleShowPricing = useCallback(() => setShowPricing(v => !v), []);

    const value = useMemo(
        () => ({
            model,
            setModel,
            models,
            chosen,
            // Prices are drawn only when the user may see them AND asked to. `pricingVisible` is the
            // server's answer; `canSeePricing` is the same rule read locally, so the toggle does not flash
            // into existence after the models request lands.
            canSeePricing: canSeePricing && pricingVisible !== false,
            showPricing: showPricing && canSeePricing,
            toggleShowPricing
        }),
        [model, models, chosen, canSeePricing, pricingVisible, showPricing, toggleShowPricing]
    );

    return <ImageAnalysisSettingsContext.Provider value={value}>{children}</ImageAnalysisSettingsContext.Provider>;
}

/** The page's model + pricing settings. Safe outside the provider (Image Management renders none of this):
 *  the defaults mean "the backend's configured model, no prices". */
export function useImageAnalysisSettings() {
    return (
        useContext(ImageAnalysisSettingsContext) || {
            model: null,
            setModel: () => {},
            models: [],
            chosen: null,
            canSeePricing: false,
            showPricing: false,
            toggleShowPricing: () => {}
        }
    );
}

export default ImageAnalysisSettingsContext;
